<template>
  <v-dialog v-model="dialogVisible" width="850" persistent scrollable no-click-animation overlay-color="grey-500">
    <v-card>
      <v-card-title class="pa-0">
        <v-toolbar flat height="46" class="transparent">
          <v-toolbar-title class="px-5 subtitle-2 grey-700--text font-weight-bold">Reglas de recepción</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-3">
              <v-btn @click="close" icon small retain-focus-on-click><v-icon color="fontBody" size="20">mdi-close</v-icon></v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
      </v-card-title>
      <v-card-text class="py-4 px-5 grey-background" style="max-height: 70vh;">
        <!-- Sección 1: Regla de la categoría -->
        <v-card flat class="mb-4 pa-4" :class="{ 'd-flex align-center': !categoryRuleEnabled, 'disabled-card': (autoRejectEnabled || referenceValidationEnabled) }" :style="!categoryRuleEnabled ? 'min-height: 80px;' : ''">
          <div class="d-flex justify-space-between align-center" :class="{ 'mb-4': categoryRuleEnabled, 'flex-grow-1': !categoryRuleEnabled }">
            <div>
              <div class="subtitle-2 grey-700--text font-weight-semibold">Regla de la categoría</div>
              <div class="body-2 mt-1 grey-500--text">Aplica exclusivamente a esta categoría</div>
            </div>
            <v-switch v-model="categoryRuleEnabled" class="ma-0 pa-0" hide-details color="success" :ripple="false" :disabled="autoRejectEnabled || referenceValidationEnabled" />
          </div>
          <div class="d-flex align-center" v-if="categoryRuleEnabled">
            <span class="body-2 grey-500--text mr-3">El documento</span>
            <v-select
              v-model="form.category_action_type"
              :items="categoryActionTypeOptions"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              style="max-width: 180px;"
              placeholder="Selecciona"
              :menu-props="{ bottom: true, offsetY: true }"
            />
            <template v-if="form.category_action_type === 'reject'">
              <span class="body-2 grey-500--text mx-3">Por</span>
              <v-select
                v-model="form.action_rule_true"
                :items="rejectReasonOptions"
                item-text="text"
                item-value="value"
                outlined
                dense
                hide-details
                class="flex-grow-0"
                :style="{ width: rejectReasonWidth }"
                placeholder="Selecciona motivo"
                :menu-props="{ bottom: true, offsetY: true }"
              />
            </template>
          </div>
        </v-card>

        <!-- Sección 2: Rechazo automático por no recepción -->
        <v-card flat class="mb-4 pa-4" :class="{ 'd-flex align-center': !autoRejectEnabled, 'disabled-card': categoryRuleEnabled }" :style="!autoRejectEnabled ? 'min-height: 80px;' : ''">
          <div class="d-flex justify-space-between align-center" :class="{ 'mb-4': autoRejectEnabled, 'flex-grow-1': !autoRejectEnabled }">
            <div>
              <div class="subtitle-2 grey-700--text font-weight-semibold">Rechazo automático por no recepción</div>
              <div class="body-2 mt-1 grey-500--text">Rechaza automáticamente facturas que estén registradas en el SII pero que no hayan sido recibidas por correo dentro del plazo definido</div>
            </div>
            <v-switch v-model="autoRejectEnabled" class="ma-0 pa-0" hide-details color="success" :ripple="false" :disabled="categoryRuleEnabled" />
          </div>
          <div class="d-flex align-center" v-if="autoRejectEnabled">
            <span class="body-2 grey-500--text mr-3">El documento se rechaza por</span>
            <v-select
              v-model="form.reject_reason_no_reception"
              :items="rejectReasonNoReceptionOptions"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              style="max-width: 200px;"
              :menu-props="{ bottom: true, offsetY: true }"
            />
            <span class="body-2 grey-500--text mx-3">a los</span>
            <v-select
              v-model="form.days_doc_not_recv"
              :items="daysOptions"
              outlined
              dense
              hide-details
              style="max-width: 80px;"
              :menu-props="{ bottom: true, offsetY: true }"
            />
            <span class="body-2 grey-500--text ml-3">{{ form.days_doc_not_recv === 1 ? 'día' : 'días' }} de espera</span>
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" size="18" class="ml-2" color="grey-300">mdi-information-outline</v-icon>
              </template>
              <span class="d-block body-2 grey-500--text pa-3">La factura será rechazada automáticamente si no se recibe por correo dentro de este plazo.</span>
            </v-tooltip>
          </div>
        </v-card>

        <!-- Sección 3: Validación de referencia del documento -->
        <v-card flat class="mb-4 pa-4" :class="{ 'd-flex align-center': !referenceValidationEnabled, 'disabled-card': categoryRuleEnabled }" :style="!referenceValidationEnabled ? 'min-height: 80px;' : ''">
          <div class="d-flex justify-space-between align-center" :class="{ 'mb-4': referenceValidationEnabled, 'flex-grow-1': !referenceValidationEnabled }">
            <div>
              <div class="subtitle-2 grey-700--text font-weight-semibold">Validación de referencia del documento</div>
              <div class="body-2 mt-1 grey-500--text">Define qué referencia debe incluir el documento</div>
            </div>
            <v-switch v-model="referenceValidationEnabled" class="ma-0 pa-0" hide-details color="success" :ripple="false" :disabled="categoryRuleEnabled" />
          </div>

          <template v-if="referenceValidationEnabled">
            <!-- Condición de validación -->
            <div class="mb-4">
              <div class="body-1 grey-500--text font-weight-medium mb-2">Condición de validación</div>
              <v-radio-group v-model="form.rule" hide-details class="ma-0 pa-0" dense>
                <v-radio value="REF_ALL" class="mb-1" dense>
                  <template v-slot:label>
                    <span class="body-2 grey-500--text">Debe tener <strong>todas</strong> las referencias</span>
                  </template>
                </v-radio>
                <v-radio value="REF_SOME" dense>
                  <template v-slot:label>
                    <span class="body-2 grey-500--text">Debe tener <strong>al menos una</strong> referencia</span>
                  </template>
                </v-radio>
              </v-radio-group>
            </div>

            <v-divider class="my-4" />

            <!-- Validación de referencia - Checkboxes -->
            <div class="mb-4">
              <div class="body-1 grey-500--text font-weight-medium mb-1">Validación de referencia</div>
              <div class="body-2 grey-300--text mb-5">Si el documento <strong>no cumple</strong> con las referencias configuradas, será <strong>rechazada</strong>. Si las <strong>cumple</strong>, podrás <strong>definir la acción</strong> a aplicar</div>
              <v-row no-gutters>
                <v-col cols="3" v-for="ref in referenceOptions" :key="ref.value">
                  <v-checkbox
                    v-model="form.parameters"
                    :value="ref.value"
                    :label="ref.text"
                    hide-details
                    class="ma-0 pa-0 mb-2"
                    dense
                  />
                </v-col>
              </v-row>
            </div>

            <!-- El documento se rechaza por -->
            <div class="d-flex align-center mb-4">
              <span class="body-2 grey-500--text mr-3">El documento se rechaza por</span>
              <v-select
                v-model="form.action_rule_false"
                :items="rejectActionOptions"
                item-text="text"
                item-value="value"
                outlined
                dense
                hide-details
                style="max-width: 200px;"
                :menu-props="{ bottom: true, offsetY: true }"
              />
              <span class="body-2 grey-500--text mx-3">en</span>
              <v-select
                v-model="form.days_rule_false"
                :items="daysApplyOptions"
                item-text="text"
                item-value="value"
                outlined
                dense
                hide-details
                class="flex-grow-0"
                :style="{ width: form.days_rule_false === 0 ? '160px' : '100px' }"
                :menu-props="{ bottom: true, offsetY: true }"
              />
              <span v-if="form.days_rule_false !== 0" class="body-2 grey-500--text ml-3">desde la recepción del documento</span>
            </div>

            <!-- Resultado de validación de referencias -->
            <v-divider class="mb-4" />
            <div class="mb-2">
              <div class="body-1 grey-500--text font-weight-medium">Resultado de validación de referencias</div>
              <div class="body-2 grey-300--text mb-3">Define qué ocurre cuando la factura cumple con las referencias configuradas</div>
            </div>
            <div class="d-flex align-center">
              <span class="body-2 grey-500--text mr-3">El documento</span>
              <v-select
                v-model="form.action_rule_true_validation"
                :items="documentActionOptions"
                item-text="text"
                item-value="value"
                outlined
                dense
                hide-details
                style="max-width: 250px;"
                placeholder="Selecciona una alternativa"
                :menu-props="{ bottom: true, offsetY: true }"
              />
            </div>
          </template>
        </v-card>
      </v-card-text>
      <v-divider />
      <v-card-actions class="py-2 px-5">
        <v-spacer />
        <v-btn @click="close" outlined>Cancelar</v-btn>
        <v-btn @click="saveRules" :loading="loadingSave" color="blue-500">Guardar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import GenericViewMixin from '@/mixins/GenericViewMixin'

export default {
  name: 'ConfigureRulesModal',
  mixins: [GenericViewMixin],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    segmentId: {
      type: String,
      default: ''
    },
    ruleData: {
      type: Object,
      default: () => null
    }
  },
  data: () => ({
    loadingSave: false,
    categoryRuleEnabled: false,
    autoRejectEnabled: false,
    referenceValidationEnabled: false,
    form: {
      rule: 'TRUE',
      category_action_type: '',
      action_rule_true: 'RCD',
      days__rule_true: 0,
      doc_not_recv: false,
      days_doc_not_recv: 1,
      reject_reason_no_reception: 'RCD',
      parameters: [],
      action_rule_false: 'RCD',
      days_rule_false: 0
    },
    categoryActionTypeOptions: [
      { text: 'Se acepta', value: 'accept' },
      { text: 'Se rechaza', value: 'reject' },
      { text: 'Sin acción', value: '' }
    ],
    rejectReasonOptions: [
      { text: 'Reclamo por contenido', value: 'RCD' },
      { text: 'Falta parcial de mercadería', value: 'RFP' },
      { text: 'Falta total de mercadería', value: 'RFT' }
    ],
    documentActionOptions: [
      { text: 'Sin acción', value: 'EMP' },
      { text: 'Aceptar', value: 'ERM' }
      // { text: 'Rechazar por contenido', value: 'RCD' },
      // { text: 'Rechazar por falta parcial de servicios o mercadería', value: 'RFP' },
      // { text: 'Rechazar por falta total de servicios o mercadería', value: 'RFT' }
    ],
    rejectReasonNoReceptionOptions: [
      { text: 'Reclamo por contenido', value: 'RCD' }
    ],
    rejectActionOptions: [
      { text: 'Reclamo por contenido', value: 'RCD' },
      { text: 'Rechazar por falta parcial', value: 'RFP' },
      { text: 'Rechazar por falta total', value: 'RFT' }
    ],
    referenceOptions: [
      { text: 'Orden de compra (801)', value: '801' },
      { text: 'Nota de pedido (802)', value: '802' },
      { text: 'Contrato (803)', value: '803' },
      { text: 'Guía de despacho (52)', value: '52' },
      { text: 'HES', value: 'HES' },
      { text: 'HEM', value: 'HEM' }
    ],
    daysApplyOptions: [
      { text: 'Inmediatamente', value: 0 },
      { text: '1 día', value: 1 },
      { text: '2 días', value: 2 },
      { text: '3 días', value: 3 },
      { text: '4 días', value: 4 },
      { text: '5 días', value: 5 },
      { text: '6 días', value: 6 },
      { text: '7 días', value: 7 }
    ]
  }),
  computed: {
    dialogVisible: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    daysOptions () {
      return Array.from({ length: 7 }, (_, i) => i + 1)
    },
    daysOptionsZeroToSeven () {
      return Array.from({ length: 8 }, (_, i) => i)
    },
    rejectReasonWidth () {
      const widths = {
        RCD: '190px',
        RFP: '210px',
        RFT: '200px'
      }
      return widths[this.form.action_rule_true] || '190px'
    }
  },
  watch: {
    value (val) {
      if (val && this.ruleData) {
        this.loadRuleData()
      } else if (val) {
        this.clear()
      }
    },
    categoryRuleEnabled (val) {
      if (val) {
        this.autoRejectEnabled = false
        this.referenceValidationEnabled = false
      }
    },
    referenceValidationEnabled (val) {
      if (val) {
        if (!this.form.rule) {
          this.form.rule = 'REF_ALL'
        }
      }
    }
  },
  methods: {
    loadRuleData () {
      if (!this.ruleData) return

      // Reset switches
      this.categoryRuleEnabled = false
      this.autoRejectEnabled = false
      this.referenceValidationEnabled = false

      // Determine which sections to enable
      // If rule is TRUE without reference parameters, it's a category rule
      if (this.ruleData.rule === 'TRUE' && (!this.ruleData.parameters || this.ruleData.parameters.length === 0)) {
        this.categoryRuleEnabled = true
      } else {
        // Switches 2 and 3 can be active simultaneously
        if (this.ruleData.doc_not_recv) {
          this.autoRejectEnabled = true
        }
        if (['REF_ALL', 'REF_SOME'].includes(this.ruleData.rule)) {
          this.referenceValidationEnabled = true
        }
      }

      // Determine category_action_type based on existing action_rule_true
      let categoryActionType = ''
      const existingAction = this.ruleData.action_rule_true
      if (existingAction === 'ERM') {
        categoryActionType = 'accept'
      } else if (['RCD', 'RFP', 'RFT'].includes(existingAction)) {
        categoryActionType = 'reject'
      }

      this.form = {
        rule: this.ruleData.rule || 'TRUE',
        category_action_type: categoryActionType,
        action_rule_true: this.ruleData.action_rule_true || 'RCD',
        days__rule_true: this.ruleData.days__rule_true || 0,
        doc_not_recv: this.ruleData.doc_not_recv ?? false,
        days_doc_not_recv: this.ruleData.days_doc_not_recv || 1,
        reject_reason_no_reception: this.ruleData.reject_reason_no_reception || 'RCD',
        parameters: this.ruleData.parameters || [],
        action_rule_false: this.ruleData.action_rule_false || 'RCD',
        days_rule_false: this.ruleData.days_rule_false || 0,
        action_rule_true_validation: this.ruleData.action_rule_true_validation || this.ruleData.action_rule_true || 'EMP'
      }
    },
    setPayload () {
      const payload = {
        segment_id: this.segmentId
      }

      // Section 1: Category rule
      if (this.categoryRuleEnabled) {
        payload.rule = 'TRUE'
        if (this.form.category_action_type === 'accept') {
          payload.action_rule_true = 'ERM'
        } else if (this.form.category_action_type === 'reject') {
          payload.action_rule_true = this.form.action_rule_true
        } else {
          payload.action_rule_true = 'EMP'
        }
      }

      // Section 2: Auto reject for non-reception (can be active along with section 3)
      if (this.autoRejectEnabled) {
        payload.doc_not_recv = true
        payload.days_doc_not_recv = this.form.days_doc_not_recv
        payload.reject_reason_no_reception = this.form.reject_reason_no_reception
      }

      // Section 3: Document reference validation (can be active along with section 2)
      if (this.referenceValidationEnabled) {
        payload.rule = this.form.rule // REF_ALL or REF_SOME
        payload.parameters = this.form.parameters
        payload.action_rule_false = this.form.action_rule_false
        payload.days_rule_false = this.form.days_rule_false
        payload.action_rule_true = this.form.action_rule_true_validation
        payload.days__rule_true = this.form.days__rule_true
      }

      return payload
    },
    validateForm () {
      if (this.referenceValidationEnabled) {
        if (!this.form.rule) {
          this.$dialog.message.error('Debe seleccionar una condición de validación')
          return false
        }
        if (!this.form.parameters || this.form.parameters.length === 0) {
          this.$dialog.message.error('Debe seleccionar al menos una referencia')
          return false
        }
      }
      return true
    },
    saveRules () {
      if (!this.validateForm()) return

      this.loadingSave = true

      this.$store.dispatch('suppliers/UPDATE', {
        resource: `segments/update-rule/${this.segmentId}`,
        payload: this.setPayload()
      })
      .then(() => {
        this.$dialog.message.info('Las reglas han sido guardadas con éxito.')
        this.$emit('saved')
        this.close()
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
      .finally(() => {
        this.loadingSave = false
      })
    },
    clear () {
      this.categoryRuleEnabled = false
      this.autoRejectEnabled = false
      this.referenceValidationEnabled = false
      this.form = {
        rule: 'TRUE',
        category_action_type: '',
        action_rule_true: 'RCD',
        days__rule_true: 0,
        doc_not_recv: false,
        days_doc_not_recv: 1,
        reject_reason_no_reception: 'RCD',
        parameters: [],
        action_rule_false: 'RCD',
        days_rule_false: 0,
        action_rule_true_validation: 'EMP'
      }
    },
    close () {
      this.clear()
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.disabled-card {
  opacity: 0.75;
  pointer-events: none;
}
</style>
